import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gamePlayService } from '../../services/db';
import { useAuth } from '../../hooks/useAuth';
import Spinner from '../../components/ui/Spinner';
import { TrophyIcon, LinkIcon } from '../../components/common/Icons';

const convertSecondsToMinutes = (seconds = 0) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const getColor = (rank) => {
  if (rank === 1) return '#F0D5BE'; // Soft gold/tan
  if (rank === 2) return '#EFECE9'; // Soft silver
  if (rank === 3) return '#F7ECE1'; // Soft bronze
  return '#FFFFFF';
};

const SeasonLeaderboardPage = () => {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [seasonDetails, setSeasonDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('weekly'); // 'weekly' | 'monthly' | 'overall'
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const loadSeason = async () => {
      try {
        const season = await gamePlayService.getSeasonDetails(seasonId);
        if (isMounted) setSeasonDetails(season);
      } catch (err) {
        console.error('Failed to load season:', err);
      }
    };
    if (seasonId) loadSeason();
    return () => {
      isMounted = false;
    };
  }, [seasonId]);

  useEffect(() => {
    let isMounted = true;
    const loadStandings = async () => {
      setLoading(true);
      try {
        const list = await gamePlayService.getSeasonLeaderboard(seasonId, activeTab);
        if (isMounted) {
          setStandings(list || []);
        }
      } catch (err) {
        console.error('Failed to load season leaderboard:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (seasonId) {
      loadStandings();
    }

    return () => {
      isMounted = false;
    };
  }, [seasonId, activeTab]);

  const currentUserEntry = user?.uid
    ? standings.find((u) => u.userId === user.uid)
    : null;

  const handleShare = () => {
    const seasonName = seasonDetails?.season || 'Season';
    const text = `🏆 I ranked #${currentUserEntry?.rank || 1} on the "${seasonName}" leaderboard with ${currentUserEntry?.score || 0} pts across ${currentUserEntry?.games || 1} games! Join the tournament on Bible Contest: https://go.biblecontest.app/season/${seasonId}`;
    if (navigator.share) {
      navigator.share({ title: 'Season Leaderboard', text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      alert('Achievement link copied to clipboard!');
    }
  };

  return (
    <div style={{ backgroundColor: '#FBF6EF', minHeight: '90vh', padding: '16px 12px 32px' }}>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        {/* Navigation & Header matching PageHeader */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' }}>
          <button
            type="button"
            className="page-header__back"
            onClick={() => navigate(`/play/season/${seasonId}`)}
            aria-label="Back to season"
          >
            ←
          </button>
          <div>
            <h1
              style={{
                fontSize: '22px',
                fontWeight: 'bold',
                color: '#332E2A',
                margin: 0,
                letterSpacing: '-0.01em',
              }}
            >
              {seasonDetails?.season || 'Season Leaderboard'}
            </h1>
            <div style={{ fontSize: '13px', color: 'rgba(51, 46, 42, 0.65)', fontWeight: 500 }}>
              Tournament Standings
            </div>
          </div>
        </div>

        {/* Toggle Switch matching Flutter toggle_switch: Weekly | Monthly | Overall */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '16px',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              backgroundColor: '#EDE8E1',
              borderRadius: '100px',
              padding: '4px',
              gap: '4px',
            }}
          >
            {[
              { id: 'weekly', label: 'Weekly' },
              { id: 'monthly', label: 'Monthly' },
              { id: 'overall', label: 'Overall' },
            ].map((tab) => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    border: 'none',
                    background: active ? '#C08552' : 'transparent',
                    color: active ? '#FFFFFF' : '#332E2A',
                    fontWeight: active ? 'bold' : '600',
                    fontSize: '13.5px',
                    padding: '8px 24px',
                    borderRadius: '100px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {/* Header Columns matching Flutter SeasonLeaderboardScreen */}
            <div
              style={{
                backgroundColor: 'rgba(192, 133, 82, 0.15)',
                borderRadius: '12px',
                padding: '10px 12px',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                color: '#332E2A',
                fontWeight: 'bold',
                fontSize: '13px',
              }}
            >
              <div style={{ width: '32px', textAlign: 'center' }}>#</div>
              <div style={{ width: '40px' }} />
              <div style={{ flex: 1, paddingLeft: '8px' }}>Name</div>
              <div style={{ width: '50px', textAlign: 'center' }}>Games</div>
              <div style={{ width: '60px', textAlign: 'center' }}>Score</div>
              <div className="lb-col-time" style={{ width: '65px', textAlign: 'center' }}>Time</div>
            </div>

            {/* Current User Rank Card (matching _buildCurrentUserRankCard) */}
            {currentUserEntry && (
              <div
                style={{
                  background: 'linear-gradient(135deg, #C08552 0%, #C8A585 100%)',
                  borderRadius: '16px',
                  padding: '14px 16px',
                  marginBottom: '14px',
                  color: '#FFFFFF',
                  boxShadow: '0 4px 14px rgba(192, 133, 82, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                {/* Rank Badge */}
                <div
                  style={{
                    backgroundColor: '#FFFFFF',
                    color: '#C08552',
                    fontWeight: 'bold',
                    fontSize: '15px',
                    borderRadius: '12px',
                    padding: '6px 10px',
                    minWidth: '40px',
                    textAlign: 'center',
                  }}
                >
                  #{currentUserEntry.rank}
                </div>

                {/* Avatar */}
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  {currentUserEntry.img ? (
                    <img
                      src={currentUserEntry.img}
                      alt={currentUserEntry.userName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                      {(currentUserEntry.userName || 'U')[0].toUpperCase()}
                    </span>
                  )}
                </div>

                {/* Name & Subtitle */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: '11px',
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontWeight: 'bold',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Your Rank
                  </div>
                  <div
                    style={{
                      fontSize: '15px',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {currentUserEntry.userName}
                  </div>
                </div>

                {/* Score & Games Count */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
                    {currentUserEntry.score} pts
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.8)' }}>
                    {currentUserEntry.games} games
                  </div>
                </div>

                {/* Share Button */}
                <button
                  type="button"
                  onClick={handleShare}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#FFFFFF',
                    marginLeft: '4px',
                  }}
                  title="Share Achievement"
                >
                  <LinkIcon size={16} />
                </button>
              </div>
            )}

            {/* Standings List */}
            {standings.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '48px 20px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  border: '1px solid #E2DCD5',
                  color: 'rgba(51, 46, 42, 0.6)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                  <TrophyIcon size={36} style={{ color: 'var(--clr-primary)' }} />
                </div>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>
                  No standings recorded yet for this {activeTab} period. Complete quiz rounds to rank!
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {standings.map((userRow) => {
                  const cardBg = getColor(userRow.rank);
                  return (
                    <div
                      key={userRow.userId || userRow.id}
                      style={{
                        backgroundColor: cardBg,
                        borderRadius: '12px',
                        border: '1px solid rgba(51, 46, 42, 0.08)',
                        padding: '10px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#332E2A',
                      }}
                    >
                      {/* Rank Index */}
                      <div
                        style={{
                          width: '32px',
                          textAlign: 'center',
                          fontWeight: 'bold',
                          fontSize: '14px',
                        }}
                      >
                        {userRow.rank}
                      </div>

                      {/* Avatar */}
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          backgroundColor: '#FFFFFF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          border: '1px solid rgba(51, 46, 42, 0.1)',
                          flexShrink: 0,
                        }}
                      >
                        {userRow.img ? (
                          <img
                            src={userRow.img}
                            alt={userRow.userName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#332E2A' }}>
                            {(userRow.userName || 'U')[0].toUpperCase()}
                          </span>
                        )}
                      </div>

                      {/* Name */}
                      <div
                        style={{
                          flex: 1,
                          paddingLeft: '10px',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {userRow.userName}
                      </div>

                      {/* Games count */}
                      <div
                        style={{
                          width: '50px',
                          textAlign: 'center',
                          fontSize: '13px',
                          color: '#332E2A',
                        }}
                      >
                        {userRow.games || 1}
                      </div>

                      {/* Score */}
                      <div
                        style={{
                          width: '60px',
                          textAlign: 'center',
                          fontWeight: 'bold',
                          fontSize: '14px',
                        }}
                      >
                        {userRow.score}
                      </div>

                      {/* Time */}
                      <div
                        className="lb-col-time"
                        style={{
                          width: '65px',
                          textAlign: 'center',
                          fontSize: '12px',
                          color: '#332E2A',
                        }}
                      >
                        {convertSecondsToMinutes(userRow.timeTaken)} min
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SeasonLeaderboardPage;
